import React, { FC, forwardRef, HTMLAttributes, ReactNode, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { TIcons } from '../../type/icons-type';
import Item from './Item';
import { useAppSelector } from '../../custom/redux/reduxHooks';
import usePermissions from '../../custom/hooks/usePermissions';

interface IListProps extends HTMLAttributes<HTMLUListElement> {
	id?: string;
	children?: ReactNode;
	className?: string;
	ariaLabelledby?: string;
	parentId?: string;
	rootId?: string;
	horizontal?: boolean;
}

export const List = forwardRef<HTMLUListElement, IListProps>(
	({ id, children, className, ariaLabelledby, parentId, rootId, horizontal, ...props }, ref) => {
		return (
			<ul
				ref={ref}
				id={id}
				className={classNames('navigation', { 'navigation-menu': horizontal }, className)}
				aria-labelledby={ariaLabelledby}
				data-bs-parent={
					parentId === `${rootId}__${rootId}`
						? `#${rootId}`
						: parentId
						? `#${parentId}`
						: null
				}
				{...props}>
				{children}
			</ul>
		);
	},
);

List.displayName = 'List';
List.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	ariaLabelledby: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string,
	horizontal: PropTypes.bool,
};
List.defaultProps = {
	id: undefined,
	children: null,
	className: undefined,
	ariaLabelledby: undefined,
	parentId: undefined,
	rootId: undefined,
	horizontal: false,
};

interface INavigationLineProps {
	className?: string;
}

export const NavigationLine: FC<INavigationLineProps> = ({ className }) => {
	return <hr className={classNames('navigation-line', className)} />;
};

NavigationLine.propTypes = {
	className: PropTypes.string,
};
NavigationLine.defaultProps = {
	className: undefined,
};

interface INavigationTitleProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	children: ReactNode;
}

export const NavigationTitle: FC<INavigationTitleProps> = ({ className, children, ...props }) => {
	return (
		<li className='navigation-item'>
			<span className={classNames('navigation-title', className)} {...props}>
				{children}
			</span>
		</li>
	);
};

NavigationTitle.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
NavigationTitle.defaultProps = {
	className: undefined,
};

interface INavigationProps {
	horizontal?: boolean;
	menu: {
		[key: string]: {
			id?: string | number;
			text?: string;
			path?: string;
			icon?: TIcons;
			isDisable?: boolean;
			subMenu?: {
				[key: string]: {
					id?: string | number;
					text?: string;
					path?: string;
					icon?: TIcons;
					isDisable?: boolean;
				};
			} | null;
			permission?: string;
		};
	};
	id: string;
	className?: string;
}

const Navigation = forwardRef<HTMLElement, INavigationProps>(
	({ menu, horizontal, id, className, ...props }, ref) => {
		const location = useLocation();
		const [activeItems, setActiveItems] = useState(new Set<string>(getActiveParents(menu, location)));
		const { t } = useTranslation('menu');

	const { hasPermission, hasHierarchicalPermission } = usePermissions();

	const handleMenuClick = (itemId: string) => {
		const topLevelIds = Object.keys(menu);
		const newActiveItems = new Set(activeItems);
		if (topLevelIds.includes(itemId)) {
			// If it's a top-level, toggle it, and if opening, close others
			if (newActiveItems.has(itemId)) {
				newActiveItems.delete(itemId);
			} else {
				// Close all top-level
				topLevelIds.forEach(tlId => newActiveItems.delete(tlId));
				newActiveItems.add(itemId);
			}
		} else {
			// For nested, just toggle
			if (newActiveItems.has(itemId)) {
				newActiveItems.delete(itemId);
			} else {
				newActiveItems.add(itemId);
			}
		}
		setActiveItems(newActiveItems);
	};

	function isItemActive(item: any, loc: any): boolean {
		if (item.path) {
			if (item.path === '/') {
				return loc.pathname === '/';
			} else {
				return loc.pathname.includes(item.path);
			}
		}
		if (item.subMenu) {
			return Object.values(item.subMenu).some((sub: any) => isItemActive(sub, loc));
		}
		return false;
	}

	function getActiveParents(menuData: any, loc: any, parents: string[] = []): string[] {
		for (const key in menuData) {
			const item = menuData[key];
			if (item.path) {
				if (item.path === '/' && loc.pathname === '/') {
					return [...parents, item.id];
				} else if (item.path !== '/' && loc.pathname.includes(item.path)) {
					return [...parents, item.id];
				}
			}
			if (item.subMenu) {
				const childActive = getActiveParents(item.subMenu, loc, [...parents, item.id]);
				if (childActive.length > 0) return childActive;
			}
		}
		return [];
	}

	function fillMenu(
		data: any,
		parentId: string,
		rootId: string,
		isHorizontal?: boolean,
		isMore?: boolean,
		parentPermission?: string,
	) {
		return Object.keys(data).map((item) => {
			const currentItem = data[item];
			
			// Check if user has permission for this menu item
			if (!hasHierarchicalPermission(currentItem.permission, parentPermission)) return null;
			
			// Don't hide parent menu even if user has no access to submenus
			// The parent should be visible if user has the parent permission
			// Submenus will be filtered out individually in the recursive call

			const isActive = isItemActive(currentItem, location);

			if (currentItem.path || currentItem.subMenu) {
				return (
					<Item
						key={currentItem.id}
						rootId={rootId}
						id={currentItem.id}
						title={currentItem.text}
						icon={currentItem.icon}
						to={currentItem.path ? `${currentItem.path}` : undefined}
						parentId={parentId}
						isHorizontal={isHorizontal}
						onClick={handleMenuClick}
						activeItems={activeItems}
						itemActive={isActive}
						notification={currentItem.notification}
						hide={currentItem.hide}>
						{!!currentItem.subMenu &&
							fillMenu(
								currentItem.subMenu,
								currentItem.id,
								rootId,
								isHorizontal,
								undefined,
								currentItem.permission,
							)}
					</Item>
				);
			} else {
				return !isMore && !isHorizontal ? (
					<NavigationTitle key={currentItem.id}>
						{t(currentItem.text)}
					</NavigationTitle>
				) : null;
			}
		}).filter(Boolean);
	}		return (
			<nav ref={ref} aria-label={id} className={className} {...props}>
				<List id={id} horizontal={horizontal}>
					{fillMenu(menu, id, id, horizontal, undefined)}
					{horizontal && (
						<Item
							rootId={`other-${id}`}
							title={t('More')}
							icon='MoreHoriz'
							isHorizontal
							isMore>
							{fillMenu(menu, `other-${id}`, `other-${id}`, false, true)}
						</Item>
					)}
				</List>
			</nav>
		);
	},
);

Navigation.displayName = 'Navigation';
Navigation.propTypes = {
	horizontal: PropTypes.bool,
	menu: PropTypes.any.isRequired,
	id: PropTypes.string.isRequired,
	className: PropTypes.string,
};
Navigation.defaultProps = {
	horizontal: false,
	className: undefined,
};

export default Navigation;
